import React, { useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from "chart.js";

import { Product, LiquidityPeriodBalanceType } from "../../../interfaces/DTO/mislaka_xml_models";
import { formatCurrency, formatCurrencyCompact, formatHandlerCode } from "../../../utils/namesFormater";
import { formatProductType } from "../../../utils/enumFormatter";
import { colorProductType, colorHandlerCode } from "../../../utils/colorEnumFormatter";
import { MimshakGroupByService } from "../../../services/MimshakGroupByService";
import { MimshakService } from "../../../services/mimshakService";
import HierarchyToggle from "../../../shared/HierarchyToggle";

ChartJS.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels);

type HierarchyValue = "handlerFirst" | "productFirst";

interface Props {
  products: Product[];
  balanceType?: LiquidityPeriodBalanceType;
  storageKey: string;
  showLegend?: boolean;
}

const GroupByProductTypeOrHandlerPieChart: React.FC<Props> = ({ products, balanceType, storageKey, showLegend = true }) => {
  const [hierarchy, setHierarchy] = useState<HierarchyValue>("handlerFirst");

  const groupedData = useMemo(() => {
    return hierarchy === "productFirst"
      ? MimshakGroupByService.groupByProductType(products, balanceType)
      : MimshakGroupByService.groupByHandlerCode(products, balanceType);
  }, [products, balanceType, hierarchy]);

  if (!groupedData || Object.keys(groupedData).length === 0) return null;

  const keys = Object.keys(groupedData);
  const labels = hierarchy === "productFirst"
    ? keys.map((type) => formatProductType(Number(type)))
    : keys.map((handlerCode) => formatHandlerCode(handlerCode));

  const values = Object.values(groupedData);
  const colors = hierarchy === "productFirst"
    ? keys.map((type) => colorProductType(Number(type)))
    : keys.map((handlerCode) => colorHandlerCode(handlerCode));

  const total = balanceType
    ? MimshakService.calculateTotalFundSum(products, balanceType)
    : MimshakService.calculateTotalsFromProducts(products);

  const data = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        hoverBackgroundColor: colors,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: showLegend },
      datalabels: {
        color: "black",
        font: { weight: "bold" as const, size: 14 },
          formatter: (value: number, context: any) => {
                      const index = context.dataIndex;
                      const rawKey = keys[index];
                      const formatted = hierarchy === "productFirst"
                        ? formatProductType(Number(rawKey))
                        : formatHandlerCode(rawKey);
                      return `${formatted}\n${formatCurrencyCompact(value)}`;
                    },
        clip: true,
      },
    },
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          התפלגות לפי {hierarchy === "productFirst" ? "סוג מוצר" : "יצרן"} | {formatCurrency(total)}
        </Typography>
        <HierarchyToggle 
           initialValue="productFirst"
            onChange={setHierarchy} 
            storageKey={storageKey} />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: "100%" }}>
          <Doughnut data={data} options={options} />
        </Box>
      </Box>
    </Paper>
  );
};

export default GroupByProductTypeOrHandlerPieChart;
